// src/services/mediaPolicy.js — the four decisions the media pipeline makes,
// separated from the machinery that carries them out.
//
// 🟥 EVERY ONE OF THESE WAS A LINE INSIDE A CACHE HELPER, and each was wrong in
//    a way that could not be seen from the line itself:
//
//      · "which entry do I drop"      — a FIFO calling itself an LRU, so the
//        photo at the top of the chat you are LOOKING AT was evicted the moment
//        120 newer decrypts happened, and its object URL revoked out from under
//        a live <img> that has no onError to notice;
//      · "is this failure worth retrying" — a CORS block, a 404 from the
//        bucket's lifecycle rule and a truncated body were indistinguishable
//        above a console.warn, so the UI offered "Try again" for media that had
//        been DELETED and offered nothing for the network blip that would have
//        succeeded on the second attempt;
//      · "how long do I wait"         — there was no second attempt at all;
//      · "how much do I keep"         — nothing was kept across a reload, so
//        every refresh re-downloaded and re-decrypted every visible byte.

/* ═══ WHICH ENTRY DO I DROP ═════════════════════════════════════════════════
   🟥 A MAP PLUS A PUSH-ONLY ARRAY IS A FIFO. `_order.push` on insert and
      `_order.shift` on evict, with a cache HIT returning early and never
      touching the order — so recency had no effect on anything and the oldest
      surviving entry went, however hard it was being used.

   🟥 AND A DUPLICATE KEY REVOKED A LIVE URL EARLY. Two concurrent misses for
      the same key — a forwarded photo, the same url in two messages, the chat
      and the info pane racing — both pushed the key, so the array held it twice
      while the map held one url. Shifting the first copy revoked the url the
      second copy is still handing out, well before the cap was reached. */

/** Move `key` to the most-recent end. Returns a NEW array; never duplicates. */
export function touch(order, key) {
  const out = (order || []).filter((k) => k !== key);
  out.push(key);
  return out;
}

/** The keys to drop so that no more than `max` remain, oldest first. */
export function evictions(order, max) {
  const list = order || [];
  const cap = Math.max(0, Number(max) || 0);
  return list.length <= cap ? [] : list.slice(0, list.length - cap);
}

/* ═══ IS THIS FAILURE WORTH RETRYING ════════════════════════════════════════
   🟥 THE TWO FAILURES LOOK IDENTICAL AND ARE OPPOSITES. R2's lifecycle rule
      deletes chat media by age (R2_SETUP.md §1.4), so a 404 means the bytes are
      gone and no amount of retrying will bring them back — offering a button
      there is a promise the product cannot keep. A CORS rejection or a dropped
      connection is the opposite: it is very likely to work on the next attempt,
      and it is exactly the case that had no retry at all.

   🟢 `status: 0` IS THE BLOCKED ONE. A cross-origin rejection never reaches
      JavaScript as a response — the browser hands `fetch` an opaque TypeError —
      so the code that catches it has no status to report and must say 0. */
export const GONE = 'gone';         // deleted or expired; there is nothing to retry
export const TRANSIENT = 'transient'; // worth another attempt
export const BROKEN = 'broken';     // the bytes arrived and would not open

export function classifyFailure(status) {
  const s = Number(status) || 0;
  /* 🟥 404 AND 410 ONLY. A 403 is what a MISCONFIGURED bucket returns, not a
     deleted object — treating it as gone would tell somebody their photo had
     expired when the real answer is that nobody has run the CORS setup. */
  if (s === 404 || s === 410) return GONE;
  return TRANSIENT;
}

/** Can the reader do anything about this? */
export const canRetry = (kind) => kind === TRANSIENT;

/* ═══ HOW LONG DO I WAIT ════════════════════════════════════════════════════
   🟢 THREE ATTEMPTS, NOT MORE. A reload asks for every visible message at once;
      a long retry ladder on a genuinely down bucket turns one broken chat into
      a minute of the tab doing nothing else. Two retries covers the blip that
      caused this report — "sometimes they show, sometimes they are broken" is
      what a burst of 200 requests against three lanes looks like — and stops
      well short of a stampede. */
export const MEDIA_ATTEMPTS = 3;

/** Milliseconds before attempt `n` (1-based). 0 for the first. */
export function retryDelay(attempt) {
  const n = Math.max(1, Math.floor(Number(attempt) || 1));
  if (n <= 1) return 0;
  /* 🟢 JITTERED, because every message on screen failed at the same moment and
     would otherwise retry at the same moment — which is the burst that caused
     the failure being retried. */
  const base = 400 * (2 ** (n - 2));          // 400, 800, 1600…
  return base + Math.floor(Math.random() * (base / 2));
}

/* ═══ HOW MUCH DO I KEEP ════════════════════════════════════════════════════
   🟥 NOTHING SURVIVED A RELOAD. The browser held decrypted bytes only as object
      URLs in one module-global Map, so every refresh re-fetched the ciphertext
      and re-ran secretbox over all of it — for a chat the reader had already
      opened twice that day. The phone has never worked this way: it writes the
      decrypted file into its document directory and links the path onto the
      message row, so the next launch paints the real file on the first frame.

   🟢 A BUDGET IN BYTES, NOT IN ENTRIES. A count is the wrong unit for a store
      holding both a 4KB document thumbnail and a 40MB clip: 120 of one is
      nothing and 120 of the other is a browser asking to be killed. */
export const MEDIA_DISK_BUDGET = 200 * 1024 * 1024;

/** How many object URLs may be alive at once. Cheap; the bytes are on disk. */
export const MEDIA_LIVE_URLS = 400;

/**
 * Which stored keys to delete so the store fits its budget, oldest use first.
 * @param entries [{ key, bytes, at }]
 */
export function overBudget(entries, budget = MEDIA_DISK_BUDGET) {
  const rows = (entries || []).filter((e) => e && e.key)
    .slice()
    .sort((a, b) => (Number(a.at) || 0) - (Number(b.at) || 0));
  /* 🟥 A ROW WITH NO SIZE MUST STILL COST SOMETHING. Charging it zero makes it
     unreclaimable — a store of rows whose `bytes` never got written would grow
     without limit and every trim would decide it was already under budget. One
     byte is enough to make it participate and too little to distort anything. */
  const cost = (e) => Math.max(1, Number(e.bytes) || 0);
  let total = rows.reduce((sum, e) => sum + cost(e), 0);
  const cap = Math.max(0, Number(budget) || 0);
  const drop = [];
  for (const e of rows) {
    if (total <= cap) break;
    drop.push(e.key);
    total -= cost(e);
  }
  return drop;
}
